import { createSelector, OutputParametricSelector } from "reselect";
import { createQuickBetslipTitleSelector } from "../../vm-builder";
import { CardProps } from "./map-to-props-factory";
import {
  getSportsbookBettingCombinations,
  getSportsbookBettingLegs,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../../../../store/state/sportsbook-betting/betting-resolver";
import { createCombinationCounterSelector } from "../../../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { isBoostedMultiple } from "@ppb/tbd-store/helpers/sportsbook-betting";
import { BettingState } from "@ppb/betslip-core";
import { getBetslipGroup } from "../../../../store/state/betslip-module/betslip-module-selectors";
import { BetslipApplicationState } from "../../../../store/state/BetslipApplicationState";
import {
  BettingGenericMetadata,
  BettingMetadata,
  BettingRacingMetadata,
  RunnersMetadata,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import { i18n } from "../../../../helpers/i18n";
import { formatTime } from "@ppb/formatters";

import type { BetslipLocalization, BetslipOddsDisplay } from "../../../../contract";

export type BetslipHeaderSelectorProps = {
  combinationId: string;
  localization: BetslipLocalization;
  oddsDisplay: BetslipOddsDisplay;
  fallbackStake?: number;
};

function getCombinationFirstLeg(
  combination: BettingState.Combination,
  legs: BettingState.LegsMap,
): BettingState.Leg | null {
  const firstLegId = combination.legs[0];

  const firstLeg = legs[firstLegId];
  if (!firstLeg) {
    return null;
  }

  return firstLeg;
}

function getCombinationFirstRunnerId(
  combination: BettingState.Combination,
  legs: BettingState.LegsMap,
): string | undefined {
  const firstLeg = getCombinationFirstLeg(combination, legs);
  if (!firstLeg) {
    return undefined;
  }

  return firstLeg.runners[0];
}

function getFirstSelectionMetadata(
  combination: BettingState.Combination,
  metadata: RunnersMetadata,
  legs: BettingState.LegsMap,
): BettingMetadata | null {
  const firstRunnerId = getCombinationFirstRunnerId(combination, legs);

  if (!firstRunnerId) return null;

  return metadata[firstRunnerId] ?? null;
}

export function getQuickBetslipSelectionMetadata(
  combination: BettingState.Combination,
  metadata: RunnersMetadata,
  legs: BettingState.LegsMap,
): BettingMetadata | null {
  if (combination.isBoosted) {
    return getFirstSelectionMetadata(combination, metadata, legs);
  }

  const legId = Object.keys(legs)
    .reverse()
    .find((id) => combination.legs.includes(id) && metadata[legs[id].runners[0]]);

  if (legId) {
    return metadata[legs[legId].runners[0]];
  }
  return null;
}

function getLegFirstRunnerMetadata(
  legs: BettingState.LegsMap,
  legId: string,
  metadata: RunnersMetadata,
): BettingMetadata | undefined {
  const leg = legs[legId];
  const firstRunnerId = leg?.runners[0];

  return metadata[firstRunnerId];
}

const isRacingMetadata = (metadata: BettingMetadata): metadata is BettingRacingMetadata => metadata.type === "RACING";

function buildPriceBoostMultipleSelectionDetails(
  combination: BettingState.Combination,
  legs: BettingState.LegsMap,
  metadata: RunnersMetadata,
): string {
  const firstSelectionMetadata = getFirstSelectionMetadata(combination, metadata, legs);

  if (!firstSelectionMetadata) {
    return "";
  }

  const eventNamesAggregation = combination.legs
    .map((legId) => getLegFirstRunnerMetadata(legs, legId, metadata))
    .filter((metadata): metadata is BettingGenericMetadata => metadata?.type === "GENERIC")
    .map((metadata) => metadata.eventName)
    .join(", ");

  return `[name]${firstSelectionMetadata.marketName}[/name]${
    eventNamesAggregation.length > 0 ? ` - ${eventNamesAggregation}` : ""
  }`;
}

function buildRacingSelectionDetails(
  combination: BettingState.Combination,
  metadata: RunnersMetadata,
  legs: BettingState.LegsMap,
  localization: BetslipLocalization,
): string {
  const selectionMetadata = getQuickBetslipSelectionMetadata(combination, metadata, legs);

  if (!selectionMetadata || !isRacingMetadata(selectionMetadata)) {
    return "";
  }

  const { racing } = selectionMetadata;
  const { localeCodeBcp47, timezone } = localization;

  const raceTime = formatTime(racing.time, localeCodeBcp47, timezone);

  const { runnerName, marketTypeName } = selectionMetadata;

  return `[name]${runnerName}[/name] - ${marketTypeName ? `${marketTypeName} - ` : ""}${raceTime} ${racing.venue}`;
}

function buildGenericSelectionDetails(
  combination: BettingState.Combination,
  metadata: RunnersMetadata,
  legs: BettingState.LegsMap,
): string {
  const selectionMetadata = getQuickBetslipSelectionMetadata(combination, metadata, legs);

  if (!selectionMetadata) {
    return "";
  }

  const { type, runnerName, marketName } = selectionMetadata;
  const eventName = type === "GENERIC" ? selectionMetadata.eventName : "";

  return `[name]${runnerName}[/name] - ${marketName}${eventName.length > 0 ? ` - ${eventName}` : ""}`;
}

const createGetQuickBetslipSelectionDetailsSelector = (): OutputParametricSelector<
  BetslipApplicationState,
  BetslipHeaderSelectorProps,
  string | null,
  (
    metadata: RunnersMetadata,
    combinations: BettingState.CombinationsMap,
    legs: BettingState.LegsMap,
    combinationId: string,
    localization: BetslipLocalization,
  ) => string | null
> => {
  const metadataSelector = (state: BetslipApplicationState): RunnersMetadata => {
    const group = getBetslipGroup(state);
    const resolvers = getBettingResolvers(group);

    return resolvers.getMetadata(state);
  };

  return createSelector(
    [
      metadataSelector,
      getSportsbookBettingCombinations,
      getSportsbookBettingLegs,
      (_: BetslipApplicationState, { combinationId }: BetslipHeaderSelectorProps) => combinationId,
      (_: BetslipApplicationState, { localization }: BetslipHeaderSelectorProps) => localization,
    ],
    (metadata, combinations, legs, combinationId, localization): string | null => {
      const combination = combinations[combinationId];
      if (!combination || !metadata) {
        return null;
      }

      const selectionMetadata = getQuickBetslipSelectionMetadata(combination, metadata, legs);
      if (!selectionMetadata) return "";

      if (combination.isBoosted) {
        return buildPriceBoostMultipleSelectionDetails(combination, legs, metadata);
      }

      if (selectionMetadata.type === "RACING") {
        return buildRacingSelectionDetails(combination, metadata, legs, localization);
      }

      return buildGenericSelectionDetails(combination, metadata, legs);
    },
  );
};

const createGetQuickBetslipMoreLabelSelector = (): OutputParametricSelector<
  BetslipApplicationState,
  BetslipHeaderSelectorProps,
  string | null,
  (totalSelections: number, combinations: BettingState.CombinationsMap, combinationId: string) => string | null
> => {
  const getCombinationCounterSelector = createCombinationCounterSelector();

  return createSelector(
    [
      (state: BetslipApplicationState, { combinationId }: BetslipHeaderSelectorProps) =>
        getCombinationCounterSelector(state, combinationId),
      getSportsbookBettingCombinations,
      (_: BetslipApplicationState, { combinationId }: BetslipHeaderSelectorProps) => combinationId,
    ],
    (totalSelections, combinations, combinationId): string | null => {
      const combination = combinations[combinationId];
      if (!combination || combination.isBoosted || totalSelections < 2) {
        return null;
      }

      return i18n({
        key: "I18N.BETSLIP.QUICK_BETSLIP.MORE",
        interpolationValues: {
          counter: totalSelections - 1,
        },
      });
    },
  );
};

export const createGetBetslipHeaderViewModel = (): OutputParametricSelector<
  BetslipApplicationState,
  BetslipHeaderSelectorProps,
  CardProps["header"],
  (
    quickBetTotalSections: number,
    title: string | null,
    selectionDetails: string | null,
    moreLabel: string | null,
    combinations: BettingState.CombinationsMap,
    combinationId: string,
  ) => CardProps["header"]
> => {
  const getCombinationCounterSelector = createCombinationCounterSelector();
  const getQuickBetslipTitleSelector = createQuickBetslipTitleSelector();
  const getQuickBetslipSelectionDetailsSelector = createGetQuickBetslipSelectionDetailsSelector();
  const getQuickBetslipMoreLabelSelector = createGetQuickBetslipMoreLabelSelector();

  return createSelector(
    [
      (state: BetslipApplicationState, { combinationId }: BetslipHeaderSelectorProps) =>
        getCombinationCounterSelector(state, combinationId),
      (
        state: BetslipApplicationState,
        { combinationId, localization, oddsDisplay, fallbackStake }: BetslipHeaderSelectorProps,
      ) => getQuickBetslipTitleSelector(state, combinationId, localization, oddsDisplay, fallbackStake),
      getQuickBetslipSelectionDetailsSelector,
      getQuickBetslipMoreLabelSelector,
      getSportsbookBettingCombinations,
      (_: BetslipApplicationState, { combinationId }: BetslipHeaderSelectorProps) => combinationId,
    ],
    (quickBetTotalSelections, title, selectionDetails, moreLabel, combinations, combinationId): CardProps["header"] => {
      const combination = combinations[combinationId];
      return {
        counter: quickBetTotalSelections,
        title: title ?? "",
        selectionDetails: selectionDetails ?? "",
        moreLabel,
        isPriceBoostMultiple: combination ? isBoostedMultiple(combination) : false,
      };
    },
  );
};
